'use client';

import { useChat } from '@/lib/hooks/useChat';
import { useEffect, useState } from 'react';

export interface AIIncidentAnalysis {
  incidentType: string;
  severity: string;
  rootCause: string;
  contributingFactors: { factor: string; weight: number }[];
  recommendedAction: string;
  confidenceScore: number;
  summary: string;
}

export function useIncidentAnalysis() {
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o-mini', false);
  const [analysis, setAnalysis] = useState<AIIncidentAnalysis | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    if (response && !isLoading) {
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]) as AIIncidentAnalysis;
          setAnalysis(parsed);
          setParseError(null);
        } else {
          setParseError('Could not parse AI response');
        }
      } catch {
        setParseError('Failed to parse AI analysis');
      }
    }
  }, [response, isLoading]);

  const analyzeIncident = (incident: {
    id: string;
    title: string;
    type: string;
    severity: string;
    node: string;
    region: string;
    detectedAt: string;
    customer: string;
    status: string;
  }) => {
    setAnalysis(null);
    setParseError(null);

    const systemPrompt = `You are a senior network operations AI assistant specializing in incident classification and root cause analysis. 
Analyze the provided network incident and return ONLY a valid JSON object with no additional text.`;

    const userPrompt = `Analyze this network incident and classify it:

Incident ID: ${incident.id}
Title: ${incident.title}
Type: ${incident.type}
Severity: ${incident.severity.toUpperCase()}
Affected Node: ${incident.node}
Region: ${incident.region}
Detected At: ${incident.detectedAt}
Customer: ${incident.customer}
Current Status: ${incident.status}

Return ONLY this JSON structure (no markdown, no extra text):
{
  "incidentType": "specific technical incident type (e.g. BGP Session Failure, Interface Saturation, etc.)",
  "severity": "Critical|High|Medium|Low",
  "rootCause": "concise root cause in 1-2 sentences",
  "contributingFactors": [
    {"factor": "factor description", "weight": 0-100},
    {"factor": "factor description", "weight": 0-100},
    {"factor": "factor description", "weight": 0-100}
  ],
  "recommendedAction": "specific actionable recommendation in 1-2 sentences",
  "confidenceScore": 0-100,
  "summary": "2-3 sentence executive summary of the incident"
}`;

    sendMessage(
      [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      { max_completion_tokens: 600 }
    );
  };

  return { analysis, isLoading, error: error?.message || parseError, analyzeIncident };
}
